import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import BackgroundEffect from './BackgroundEffect';

const Register = () => {
    const [step, setStep] = useState(1); // 1: Register, 2: OTP
    const [formData, setFormData] = useState({
        username: '',
        phone: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const navigate = useNavigate();

    // Timer effect for Resend OTP
    React.useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            const val = value.replace(/\D/g, '');
            if (val.length <= 10) {
                setFormData({ ...formData, [name]: val });
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            return setError('Passwords do not match');
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post(`${BASE_URL}/users/createUser.php`, {
                user_name: formData.username,
                mobile_no: formData.phone,
                user_password: formData.password
            });
            if (response.data.status === 201) {
                setSuccessMsg('Registration successful! Please enter the OTP sent to your mobile.');
                // For development: log the OTP if it exists in the response
                if (response.data.otp_code) {
                    console.log('Development OTP:', response.data.otp_code);
                }
                setStep(2);
            } else {
                setError(response.data.message || 'Registration failed');
            }
        } catch (err) {
            if (err.response) {
                const status = err.response.status;
                const message = err.response.data?.message || err.message;
                setError(`Server Error ${status}: ${message} (${err.config.url})`);
            } else if (err.request) {
                setError('No response from server. Check if your backend is running at ' + BASE_URL);
            } else {
                setError('Request Error: ' + err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post(`${BASE_URL}/users/verifyOtp.php`, {
                mobile_no: formData.phone,
                otp_code: otp
            });

            if (response.data.status === 200) {
                setSuccessMsg('Phone verified successfully! Redirecting to login...');
                setTimeout(() => navigate('/login'), 2000);
            } else {
                setError(response.data.message || 'Verification failed');
            }
        } catch (err) {
            if (err.response) {
                const status = err.response.status;
                const message = err.response.data?.message || err.message;
                setError(`Server Error ${status}: ${message} (${err.config.url})`);
            } else if (err.request) {
                setError('No response from server. Check if your backend is running at ' + BASE_URL);
            } else {
                setError('Request Error: ' + err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };
    const handleResendOtp = async () => {
        if (resendTimer > 0) return;
        
        setIsLoading(true);
        setError('');
        setSuccessMsg('');

        try {
            const response = await axios.post(`${BASE_URL}/users/sendOtp.php`, {
                mobile_no: formData.phone,
                resend: true
            });

            if (response.data.status === 200) {
                setSuccessMsg('OTP resent successfully!');
                if (response.data.otp_code) {
                    console.log('Resent Development OTP:', response.data.otp_code);
                }
                setResendTimer(60); // 60 seconds cooldown
            } else {
                setError(response.data.message || 'Failed to resend OTP');
            }
        } catch (err) {
            setError('Error resending OTP: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-container d-flex align-items-center justify-content-center">
            <BackgroundEffect />
            <div className="auth-card p-4 p-md-5">
                <div className="text-center mb-4">
                    <h2 className="section-head-main mb-2" style={{ fontSize: '2rem' }}>
                        {step === 1 ? 'CREATE ACCOUNT' : 'VERIFY PHONE'}
                    </h2>
                    <div style={{ width: '40px', height: '2px', backgroundColor: 'var(--secondary-color)', margin: '0 auto' }}></div>
                </div>

                {error && <div className="alert alert-danger bg-transparent text-danger border-danger small p-2 mb-4">{error}</div>}
                {successMsg && <div className="alert alert-success bg-transparent text-success border-success small p-2 mb-4">{successMsg}</div>}

                {step === 1 ? (
                    <form onSubmit={handleRegister}>
                        <div className="mb-3">
                            <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>FULL NAME</label>
                            <div className="auth-input-wrapper">
                                <input
                                    name="username"
                                    type="text"
                                    className="auth-input"
                                    placeholder="John Doe"
                                    value={formData.username}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>PHONE NUMBER</label>
                            <div className="auth-input-wrapper">
                                <input
                                    name="phone"
                                    type="tel"
                                    className="auth-input w-100"
                                    placeholder="Enter your phone number"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>PASSWORD</label>
                            <div className="auth-input-wrapper">
                                <input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    className="auth-input"
                                    placeholder="Min. 8 characters"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>CONFIRM PASSWORD</label>
                            <div className="auth-input-wrapper">
                                <input
                                    name="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="auth-input"
                                    placeholder="Repeat your password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="btn-book-now w-100 py-3" disabled={isLoading}>
                            {isLoading ? 'CREATING...' : 'CREATE ACCOUNT'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOtp}>
                        <div className="mb-5">
                            <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>ENTER 4-DIGIT OTP</label>
                            <div className="auth-input-wrapper">
                                <input
                                    type="text"
                                    className="auth-input text-center"
                                    placeholder="0000"
                                    maxLength="4"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    required
                                    style={{ letterSpacing: '10px', fontSize: '1.5rem' }}
                                />
                            </div>
                            <p className="text-center mt-3 small" style={{ color: 'var(--text-secondary)' }}>
                                OTP sent to {formData.phone}
                            </p>
                            <div className="text-center mt-2">
                                <button
                                    type="button"
                                    className="bg-transparent border-0 small"
                                    style={{ 
                                        color: resendTimer > 0 ? 'var(--text-secondary)' : 'var(--secondary-color)', 
                                        textDecoration: 'none',
                                        cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={handleResendOtp}
                                    disabled={resendTimer > 0 || isLoading}
                                >
                                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Didn't receive OTP? Resend"}
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="btn-book-now w-100 py-3" disabled={isLoading}>
                            {isLoading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
                        </button>

                        <div className="text-center mt-3">
                            <button
                                type="button"
                                className="bg-transparent border-0 small"
                                style={{ color: 'var(--secondary-color)', textDecoration: 'none' }}
                                onClick={() => setStep(1)}
                            >
                                ← Back to Registration
                            </button>
                        </div>
                    </form>
                )}

                {step === 1 && (
                    <div className="text-center mt-4 pt-2">
                        <p className="m-0" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                            Already have an account? <Link to="/login" style={{ color: 'var(--secondary-color)', textDecoration: 'none' }}>Login instead</Link>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Register;
